import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { captionOpacityAt, songInfoOpacityAt } from 'fym-shared/src/music24/musicVideoEffectFrames';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { editorMessages } from '../messages/editorMessages';
import { endCardLineMessages } from '../messages/panelMessages';
import { captionAt, captionWordAt, effectOpacities, isInSpan, restingBlackOpacity, textSpans } from './previewClock';
import { isLineShownByEffect, previewLyricLines } from './previewEffectsMath';
import styles from './PreviewOverlays.module.scss';

const CAPTION_STYLE_CLASS = {
  clean: undefined,
  caps: styles.captionCaps,
  serif: styles.captionSerif,
  word: styles.captionWord,
} as const;

interface PreviewOverlaysProps {
  /** The second of the song on screen (the rendered play position). */
  sec: number;
  showSafe: boolean;
}

/**
 * Everything over the footage of the live preview (design b1, d5): grain, dark corners, the
 * flashes, the light leaks, the fades, the lyric captions with the 0.2 s lead (not the line of a
 * lyric moment), the title, the name, the corner info, the custom texts, the end card and the safe
 * area. The fonts are the design fonts; the server render uses the fonts of the release theme.
 */
const PreviewOverlaysComponent: React.FC<PreviewOverlaysProps> = ({ sec, showSafe }) => {
  const intl = useIntl();
  const { doc, data, songMap, transport } = useMusicVideoEditor();
  const { texts, lyrics, look, settings } = doc;
  const spans = useMemo(() => textSpans(settings, songMap.downbeats), [settings, songMap.downbeats]);
  const clip = useMemo(
    () => doc.clips.find((item) => sec >= item.startSec && sec < item.endSec) ?? null,
    [doc.clips, sec]
  );
  const effects = effectOpacities(doc, songMap, clip, sec);
  const black = restingBlackOpacity(effects.black, settings, sec, transport.isPlaying || transport.isLoading);
  const timedLines = useMemo(() => previewLyricLines(data.lyrics.lines), [data.lyrics.lines]);
  const anyCaption = lyrics.on
    ? captionAt(data.lyrics.lines, sec, { when: lyrics.when, sections: songMap.sections })
    : null;
  // A lyric moment shows the words of its line itself, so the caption of that line goes (the render rule).
  const captionIndex = anyCaption?.index ?? null;
  const captionLine = captionIndex === null ? undefined : timedLines.find((line) => line.index === captionIndex);
  const caption =
    anyCaption !== null && captionLine !== undefined && isLineShownByEffect(captionLine, doc.effects)
      ? null
      : anyCaption;
  const nextStart = useMemo(() => {
    if (caption === null) {
      return null;
    }
    return data.lyrics.lines.find((line) => line.index > caption.index && line.startMs !== null)?.startMs ?? null;
  }, [caption, data.lyrics.lines]);
  const captionText =
    caption === null ? null : lyrics.style === 'word' ? captionWordAt(caption, nextStart, sec) : caption.text;
  // The rules of the render: the captions yield to a lyric moment, and the title, the name and the
  // corner yield to a title card or an artist card, so no text shows twice in one place.
  const captionOpacity = captionOpacityAt(doc.effects, sec);
  const infoOpacity = songInfoOpacityAt(doc.effects, sec);
  const infoStyle = infoOpacity < 1 ? { opacity: infoOpacity } : undefined;
  const title = texts.title.text === '' ? data.song.title : texts.title.text;
  const artist = texts.name.text === '' ? data.song.artistName : texts.name.text;
  const custom = texts.custom.filter((item) => sec >= item.startSec && sec < item.endSec);
  const showEnd = texts.endCard.on && isInSpan(spans.endCard, sec);

  return (
    <>
      {look.grain && <div className={styles.grain} />}
      {look.vignette && <div className={styles.vignette} />}
      {effects.leak > 0 && <div className={styles.leak} style={{ opacity: effects.leak }} />}
      {!showEnd && captionText !== null && captionOpacity > 0 && (
        <div
          style={captionOpacity < 1 ? { opacity: captionOpacity } : undefined}
          className={clsx(
            styles.caption,
            CAPTION_STYLE_CLASS[lyrics.style],
            (lyrics.where === 'middle' || lyrics.style === 'word') && styles.captionMiddle
          )}
        >
          {captionText}
        </div>
      )}
      {texts.title.on && isInSpan(spans.title, sec) && infoOpacity > 0 && (
        <div className={styles.title} style={infoStyle}>
          <b>{title}</b>
          <small>{data.song.artistName}</small>
        </div>
      )}
      {texts.name.on && isInSpan(spans.name, sec) && infoOpacity > 0 && (
        <div className={styles.name} style={infoStyle}>
          {artist} — {data.song.title}
        </div>
      )}
      {texts.songInfoCorner && !showEnd && infoOpacity > 0 && (
        <div className={styles.corner} style={infoStyle}>
          {data.song.artistName} · {data.song.title}
        </div>
      )}
      {custom.map((item) => (
        <div key={item.id} className={styles.custom}>
          {item.text}
        </div>
      ))}
      {showEnd && (
        <div className={styles.endCard}>
          <div
            className={styles.endCover}
            style={data.song.artworkUrl === null ? undefined : { backgroundImage: `url("${data.song.artworkUrl}")` }}
          />
          <div className={styles.endText}>
            <span>{intl.formatMessage(endCardLineMessages[texts.endCard.line])}</span>
            <b>{data.song.title}</b>
            {data.song.smartLinkUuid !== null && <i>{intl.formatMessage(editorMessages.endCardLink)}</i>}
          </div>
        </div>
      )}
      {effects.flash > 0 && <div className={styles.flash} style={{ opacity: effects.flash * 0.85 }} />}
      {black > 0 && <div className={styles.black} style={{ opacity: black }} />}
      {showSafe && <div className={styles.safe} />}
    </>
  );
};

export const PreviewOverlays = TypedMemo(PreviewOverlaysComponent);
